import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { CheckCircle, Error, Warning, Info } from "@mui/icons-material";
import { api } from "../api/axios";
import { IconTile, PageHeader, Pill } from "../components/ui";

export default function PaymentSettings() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const testConnection = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/api/payment/test-connection");
      setResult(data);
    } catch (err) {
      setResult({
        ok: false,
        error: err?.response?.data?.message || err?.message || "שגיאה בבדיקת החיבור",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    testConnection();
  }, []);

  const getStatusIcon = () => {
    if (!result) return null;
    if (result.ok) return <IconTile tone="success" size={52}><CheckCircle /></IconTile>;
    if (result.issues?.length) return <IconTile tone="warning" size={52}><Warning /></IconTile>;
    return <IconTile tone="error" size={52}><Error /></IconTile>;
  };

  const getStatusColor = () => {
    if (!result) return "default";
    if (result.ok) return "success";
    if (result.issues?.length) return "warning";
    return "error";
  };

  return (
    <Box sx={{ maxWidth: 880 }}>
      <PageHeader title="הגדרות תשלום - SmartPay" subtitle="בדיקת חיבור והגדרת ספק התשלום" />

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            {loading ? <CircularProgress size={44} /> : getStatusIcon()}
            <Box>
              <Typography variant="h6" component="h2">
                {loading ? "בודק חיבור..." : result?.ok ? "החיבור תקין ✓" : "יש בעיה בחיבור"}
              </Typography>
              <Pill tone={result?.mode === "demo" ? "info" : "primary"} sx={{ mt: 0.75 }}>
                {result?.mode === "demo" ? "מצב Demo" : "מצב Production"}
              </Pill>
            </Box>
          </Box>

          {result?.message && (
            <Alert severity={getStatusColor()} sx={{ mb: 2 }}>
              {result.message}
            </Alert>
          )}

          {result?.mode === "demo" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>מצב בדיקה פעיל</strong>
              </Typography>
              <Typography variant="body2">
                התשלומים עובדים במצב סימולציה. לא יתבצעו חיובים אמיתיים.
                <br />
                כדי להפעיל תשלומים אמיתיים, הגדר את משתני הסביבה של SmartPay בשרת.
              </Typography>
            </Alert>
          )}

          {result?.mode === "production" && !result?.configured && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>משתני סביבה חסרים:</strong>
              </Typography>
              <List dense>
                {result?.missing?.map((item) => (
                  <ListItem key={item} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Error color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={<code style={{ fontSize: 13 }}>{item}</code>}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {result?.issues?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>בעיות בפורמט Credentials:</strong>
              </Typography>
              <List dense>
                {result.issues.map((issue, idx) => (
                  <ListItem key={idx} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Warning color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={issue} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {result?.note && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {result.note}
            </Alert>
          )}

          {result?.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>שגיאה:</strong> {result.error}
            </Alert>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={testConnection}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            >
              בדוק חיבור שוב
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <IconTile size={36} tone="info"><Info /></IconTile>
            <Typography variant="h6" component="h2">מדריך הגדרה</Typography>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            כדי להפעיל תשלומים אמיתיים דרך SmartPay, צריך להגדיר את משתני הסביבה הבאים בקובץ <code>.env</code> בשרת:
          </Typography>

          <Box dir="ltr" sx={{ bgcolor: "#0f172a", color: "#e2e8f0", p: 2, borderRadius: 1.5, fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 13, textAlign: "start" }}>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
{`# שנה מ-demo ל-production
PAYMENT_MODE=production

# הזן את הערכים שקיבלת מ-SmartPay (מהPostman collection)
SMARTPAY_API_URL=https://app11.smartpay.co.il/v1
SMARTPAY_ADMIN_UUID=your_admin_uuid_here
SMARTPAY_ADMIN_SECRET_KEY=your_admin_secret_key_here
SMARTPAY_PAGE_UUID=your_page_uuid_here
SMARTPAY_WEBHOOK_SECRET=your_webhook_secret_here`}
            </pre>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>חשוב:</strong> אחרי שינוי קובץ <code>.env</code>, יש להפעיל מחדש את השרת!
            </Typography>
          </Alert>

          <Typography variant="body2" sx={{ mt: 2 }}>
            למדריך מפורט, ראה: <code>docs/SMARTPAY_SETUP.md</code>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
